'use client';

import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from 'react';

interface ExpenseState {
  isCreateExpensePopupOpen: boolean;
  isEditExpensePopupOpen: boolean;
  expenseId: string | null;
}

type ExpenseAction =
  | { type: 'OPEN_CREATE_EXPENSE_POPUP' }
  | { type: 'CLOSE_CREATE_EXPENSE_POPUP' }
  | { type: 'OPEN_EDIT_EXPENSE_POPUP'; payload: { expenseId: string } }
  | { type: 'CLOSE_EDIT_EXPENSE_POPUP' };

const initialState: ExpenseState = {
  isCreateExpensePopupOpen: false,
  isEditExpensePopupOpen: false,
  expenseId: null,
};

const ExpenseContext = createContext<{
  state: ExpenseState;
  dispatch: Dispatch<ExpenseAction>;
} | null>(null);

function expenseReducer(
  state: ExpenseState,
  action: ExpenseAction
): ExpenseState {
  switch (action.type) {
    case 'OPEN_CREATE_EXPENSE_POPUP':
      return { ...state, isCreateExpensePopupOpen: true };
    case 'CLOSE_CREATE_EXPENSE_POPUP':
      return { ...state, isCreateExpensePopupOpen: false };
    case 'OPEN_EDIT_EXPENSE_POPUP':
      return {
        ...state,
        isEditExpensePopupOpen: true,
        expenseId: action.payload.expenseId,
      };
    case 'CLOSE_EDIT_EXPENSE_POPUP':
      return { ...state, isEditExpensePopupOpen: false, expenseId: null };
    default:
      return state;
  }
}

export const ExpenseProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState);
  return (
    <ExpenseContext.Provider value={{ state, dispatch }}>
      {children}
    </ExpenseContext.Provider>
  );
};

export const useExpenseContext = () => {
  const context = useContext(ExpenseContext);
  if (!context)
    throw new Error('useExpenseContext must be used within ExpenseProvider');
  return context;
};
